// Pure helper functions for selecting the richest agent output content across
// timeline entry types (STATUS, STEP_RESULT, PLAN). Extracted so this selection
// logic can be unit tested without rendering full React components or mocking
// GraphQL subscriptions/queries.

/**
 * Extracts a display-worthy text string from a STEP_RESULT payload.
 * Tries `output`, `message`, `detail` fields in that order. If the value is
 * itself an object, it is JSON-stringified as a last resort.
 *
 * @param {*} parsedPayload - Already-parsed (JSON.parse'd) STEP_RESULT data.
 * @returns {string}
 */
export const extractStepResultText = (parsedPayload) => {
  if (parsedPayload == null) return '';
  if (typeof parsedPayload === 'string') return parsedPayload;
  if (typeof parsedPayload !== 'object') return '';

  let text = parsedPayload.output || parsedPayload.message || parsedPayload.detail || '';
  if (text && typeof text === 'object') {
    try {
      text = JSON.stringify(text, null, 2);
    } catch {
      text = '';
    }
  }
  return typeof text === 'string' ? text : '';
};

/**
 * Extracts the `detail` field from a STATUS entry payload whose status is
 * COMPLETED. Returns '' if the payload isn't a COMPLETED status or has no detail.
 *
 * @param {*} parsedStatusPayload - Already-parsed STATUS entry data.
 * @returns {string}
 */
export const extractCompletedStatusDetail = (parsedStatusPayload) => {
  if (!parsedStatusPayload || typeof parsedStatusPayload !== 'object') return '';
  const status = (parsedStatusPayload.status || '').toString().toUpperCase();
  if (status !== 'COMPLETED') return '';
  return typeof parsedStatusPayload.detail === 'string' ? parsedStatusPayload.detail : '';
};

/**
 * Extracts a display-worthy text string from a PLAN entry payload. Tries
 * `plan`, `markdown`, `content`, `output`, `message`, `detail` fields, falling
 * back to JSON.stringify for arbitrary objects.
 *
 * @param {*} parsedPlanPayload - Already-parsed PLAN entry data.
 * @returns {string}
 */
export const extractPlanText = (parsedPlanPayload) => {
  if (parsedPlanPayload == null) return '';
  if (typeof parsedPlanPayload === 'string') return parsedPlanPayload;
  if (typeof parsedPlanPayload !== 'object') return '';

  const text =
    parsedPlanPayload.plan ||
    parsedPlanPayload.markdown ||
    parsedPlanPayload.content ||
    parsedPlanPayload.output ||
    parsedPlanPayload.message ||
    parsedPlanPayload.detail;
  if (typeof text === 'string') return text;
  try {
    return JSON.stringify(parsedPlanPayload);
  } catch {
    return '';
  }
};

/**
 * Selects the richest (longest) content among a COMPLETED STATUS detail and a
 * list of STEP_RESULT entries. This mirrors the real-world case where the
 * PLAN entry only contains a short summary but the full agent analysis
 * (Genie response with tables/SQL) lives in a STEP_RESULT or STATUS detail.
 *
 * @param {object} params
 * @param {string} params.completedDetail - Text from extractCompletedStatusDetail.
 * @param {Array<{data: string|object}>} params.stepResultItems - Raw STEP_RESULT
 *   timeline entries (data may be a JSON string or already-parsed object).
 * @param {(raw: *) => *} params.parseFn - Function to parse each entry's raw `data`
 *   field into a JS value (handles JSON strings, DynamoDB unmarshal, etc.)
 * @returns {string} The longest candidate text, or '' if none found.
 */
export const selectBestEnrichedContent = ({ completedDetail = '', stepResultItems = [], parseFn }) => {
  let best = typeof completedDetail === 'string' ? completedDetail : '';

  for (const item of stepResultItems) {
    const parsed = typeof parseFn === 'function' ? parseFn(item?.data) : item?.data;
    const text = extractStepResultText(parsed);
    if (typeof text === 'string' && text.length > best.length) {
      best = text;
    }
  }

  return best;
};

/**
 * Decides whether a newly-arrived PLAN entry (e.g. from a real-time
 * subscription) should replace the currently-displayed plan content, given
 * previously-derived "enriched" content that may be richer than either.
 *
 * Rules:
 *  - If there is no previous plan entry, prefer the enriched content when it
 *    is richer than the incoming entry's raw data length; otherwise use the
 *    incoming entry as-is.
 *  - If there is a previous plan entry, keep whichever of (previous, incoming)
 *    has more content — never regress to a shorter value.
 *
 * @param {object} params
 * @param {{data: string}|null} params.prev - Currently displayed plan entry (or null).
 * @param {{data: string}} params.incoming - Newly arrived PLAN entry from subscription.
 * @param {string} params.enrichedContent - Best known enriched content (may be '').
 * @returns {{data: string}} The entry (possibly with `data` overridden) to display.
 */
export const resolveIncomingPlanEntry = ({ prev, incoming, enrichedContent = '' }) => {
  const incomingLen = (incoming?.data || '').length;

  if (!prev) {
    if (enrichedContent && enrichedContent.length > incomingLen) {
      return { ...incoming, data: enrichedContent };
    }
    return incoming;
  }

  const prevLen = (prev?.data || '').length;
  return incomingLen > prevLen ? incoming : prev;
};
